import React, { useId, useRef, useState } from 'react';
import {
  ArrowReturnLeft,
  BarChartLine,
  Bell,
  ChatSquareText,
  Collection,
  GraphUpArrow,
  Megaphone,
  PersonCheck,
  Speedometer2,
} from 'react-bootstrap-icons';
import { EMBED_ORIGIN, IFRAME_ALLOW, IFRAME_SANDBOX } from '../../components/embed/embedCode';
import { useLocale } from '../../i18n/LocaleContext';

// The pieces of the signed-out landing (pages/Login.js): the Outfino lockup,
// the example webshop page in a mini browser window with a preview of the
// fit-check button, and the two benefit groups as chips floating beside it.

/// The button on the mock page: a preview, not the app — the real page from
/// embed.outfino.io as the Beállítások previews load it (demo=1: no key, no
/// config card; analytics=0: never counted), and nothing in it can be clicked.
/// theme: the button's own — dark on the light page, light on the dark one.
export function landingPreviewSrc(isDark = false) {
  return `${EMBED_ORIGIN}/button.html?demo=1&analytics=0&lang=auto&theme=${isDark ? 'light' : 'dark'}&variant=full&shape=pill&h=48`;
}
export const LANDING_PREVIEW_SRC = landingPreviewSrc(false);

/// The round Outfino mark (the embed's logoSvg, as outfino-web draws it): the
/// brand-gradient disc with the white ring, and a sheen on hover.
export function LogoDisc({ size = 40 }) {
  const id = useId().replace(/:/g, '');
  return (
    <svg className="pl-disc" width={size} height={size} viewBox="0 0 40 40" aria-hidden="true" focusable="false">
      <defs>
        <linearGradient id={`g${id}`} x1="1" y1="1" x2="39" y2="39" gradientUnits="userSpaceOnUse">
          <stop offset="0" stopColor="#ffe8c7" />
          <stop offset=".22" stopColor="#4fcff8" />
          <stop offset=".52" stopColor="#7f8fee" />
          <stop offset=".8" stopColor="#ab5dec" />
          <stop offset="1" stopColor="#1f4fe8" />
        </linearGradient>
        <linearGradient id={`s${id}`} x1="0" y1="0" x2="1" y2="0">
          <stop offset="0" stopColor="#fff" stopOpacity="0" />
          <stop offset=".5" stopColor="#fff" stopOpacity=".55" />
          <stop offset="1" stopColor="#fff" stopOpacity="0" />
        </linearGradient>
        <clipPath id={`c${id}`}>
          <circle cx="20" cy="20" r="20" />
        </clipPath>
      </defs>
      <circle cx="20" cy="20" r="20" fill={`url(#g${id})`} />
      <g clipPath={`url(#c${id})`}>
        <g transform="rotate(24 20 20)">
          <rect className="pl-disc__sheen" x="-26" y="-12" width="14" height="64" fill={`url(#s${id})`} />
        </g>
      </g>
      <circle cx="20" cy="20" r="11.1" fill="none" stroke="#fff" strokeWidth="2.2" />
    </svg>
  );
}

export function Lockup() {
  const { t } = useLocale();
  return (
    <span className="pl-lockup">
      <LogoDisc size={36} />
      <span className="pl-lockup__name">Outfino</span>
      <span className="pl-lockup__tag">{t('landing.partners')}</span>
    </span>
  );
}

/// A neutral product: a knit sweater, flat, in oat — drawn, not a photo of
/// anybody's goods.
function SweaterArt() {
  return (
    <svg className="pl-shop__art" viewBox="0 0 240 200" aria-hidden="true" focusable="false">
      <ellipse cx="120" cy="182" rx="74" ry="7" className="pl-art-shadow" />
      <g strokeLinejoin="round" strokeLinecap="round">
        {/* sleeves */}
        <path d="M72 46 L40 120 Q38 128 46 131 L60 136 Q66 137 68 131 L84 88 Z" fill="#e4d5c1" />
        <path d="M168 46 L200 120 Q202 128 194 131 L180 136 Q174 137 172 131 L156 88 Z" fill="#e4d5c1" />
        {/* cuffs */}
        <path d="M44 124 L64 132 L60 142 Q58 146 53 144 L41 139 Q37 137 39 133 Z" fill="#d6c3aa" />
        <path d="M196 124 L176 132 L180 142 Q182 146 187 144 L199 139 Q203 137 201 133 Z" fill="#d6c3aa" />
        {/* body */}
        <path d="M72 46 Q78 38 96 34 Q120 50 144 34 Q162 38 168 46 L162 90 L164 164 Q164 170 158 170 L82 170 Q76 170 76 164 L78 90 Z" fill="#ece0cf" />
        {/* knit texture */}
        <g stroke="#dccbb4" strokeWidth="1.4" fill="none" opacity=".9">
          <path d="M100 60 V156" />
          <path d="M120 64 V156" />
          <path d="M140 60 V156" />
          <path d="M100 76 l4 5 l4 -5 M100 96 l4 5 l4 -5 M100 116 l4 5 l4 -5 M100 136 l4 5 l4 -5" />
          <path d="M132 76 l4 5 l4 -5 M132 96 l4 5 l4 -5 M132 116 l4 5 l4 -5 M132 136 l4 5 l4 -5" />
        </g>
        {/* hem */}
        <path d="M76 154 L164 154 L164 164 Q164 172 156 172 L84 172 Q76 172 76 164 Z" fill="#d6c3aa" />
        <g stroke="#c8b295" strokeWidth="1.2">
          {Array.from({ length: 21 }, (_, i) => (
            <path key={i} d={`M${80 + i * 4} 157 V169`} />
          ))}
        </g>
        {/* neck rib */}
        <path d="M94 34 Q120 52 146 34 Q144 30 140 30 Q120 44 100 30 Q96 30 94 34 Z" fill="#d6c3aa" />
      </g>
    </svg>
  );
}

/// The live button, framed from embed.outfino.io — to be seen, not used: no
/// pointer events, out of the tab order and hidden from screen readers (the
/// window's label says what it is).
function PreviewFrame({ isDark }) {
  const [loaded, setLoaded] = useState(false);
  const src = landingPreviewSrc(isDark);
  return (
    <div className={`pl-embed${loaded ? ' is-loaded' : ''}`} aria-hidden="true">
      <iframe
        key={src}
        className="pl-embed__frame"
        src={src}
        title="Outfino"
        data-landing-preview="1"
        scrolling="no"
        referrerPolicy="no-referrer"
        sandbox={IFRAME_SANDBOX}
        allow={IFRAME_ALLOW}
        tabIndex={-1}
        onLoad={() => setLoaded(true)}
      />
    </div>
  );
}

/// The example webshop page in a small browser window: a drawn product (no
/// real brand), its price, sizes and "add to cart", and right under them the
/// embed — the part a visitor can try.
export function ShopWindow({ isDark = false }) {
  const { t } = useLocale();
  return (
    <figure className="pl-browser" aria-label={t('landing.demoLabel')}>
      <div className="pl-browser__bar" aria-hidden="true">
        <span className="pl-browser__dots">
          <i />
          <i />
          <i />
        </span>
        <span className="pl-browser__url">
          <svg viewBox="0 0 16 16" width="10" height="10" aria-hidden="true">
            <path
              fill="currentColor"
              d="M8 1a3 3 0 0 0-3 3v2H4a1 1 0 0 0-1 1v6a1 1 0 0 0 1 1h8a1 1 0 0 0 1-1V7a1 1 0 0 0-1-1h-1V4a3 3 0 0 0-3-3Zm-2 5V4a2 2 0 1 1 4 0v2H6Z"
            />
          </svg>
          {t('landing.shopUrl')}
        </span>
      </div>
      <div className="pl-shop">
        <div className="pl-shop__photo">
          <SweaterArt />
          {/* phone only: two of the visitor's benefits, over the photo's corners */}
          <span className="pl-photo-chip pl-photo-chip--a" aria-hidden="true">
            <span className="pl-chip__icon">
              <Speedometer2 />
            </span>
            {t('landing.visitor.match.title')}
          </span>
          <span className="pl-photo-chip pl-photo-chip--b" aria-hidden="true">
            <span className="pl-chip__icon">
              <Collection />
            </span>
            {t('landing.visitor.wardrobe.title')}
          </span>
        </div>
        <div className="pl-shop__info">
          <span className="pl-shop__crumb">{t('landing.productCrumb')}</span>
          <span className="pl-shop__name">{t('landing.productName')}</span>
          <span className="pl-shop__price">{t('landing.productPrice')}</span>
          <span className="pl-shop__sizes" aria-hidden="true">
            {['XS', 'S', 'M', 'L', 'XL'].map((size) => (
              <i key={size} className={size === 'M' ? 'is-on' : undefined}>
                {size}
              </i>
            ))}
          </span>
          <span className="pl-shop__cart" aria-hidden="true">
            {t('landing.addToCart')}
          </span>
          <PreviewFrame isDark={isDark} />
        </div>
      </div>
    </figure>
  );
}

const VISITOR_TILES = [
  { key: 'match', Icon: Speedometer2 },
  { key: 'why', Icon: ChatSquareText },
  { key: 'wardrobe', Icon: Collection },
  { key: 'follow', Icon: Bell },
];
const BRAND_TILES = [
  { key: 'returns', Icon: ArrowReturnLeft },
  { key: 'quality', Icon: PersonCheck },
  { key: 'conversion', Icon: GraphUpArrow },
  { key: 'reach', Icon: Megaphone },
  { key: 'insights', Icon: BarChartLine },
];

/// One group: a small heading and its benefits as icon + title chips. The
/// chip's one line is not on the page; it shows as a small tip on hover or
/// focus (a tap focuses on touch), and is the chip's description for screen
/// readers. The tip is placed inside the group's box, so it never leaves the
/// screen.
function TileGroup({ id, tiles }) {
  const { t } = useLocale();
  const box = useRef(null);
  const [tip, setTip] = useState(null); // { key, left, top }

  function show(key, el) {
    const group = box.current;
    if (!group || !el) return;
    const g = group.getBoundingClientRect();
    const r = el.getBoundingClientRect();
    setTip({ key, x: r.left - g.left + r.width / 2, top: r.bottom - g.top + 6, width: g.width });
  }
  function hide(key) {
    setTip((cur) => (cur && cur.key === key ? null : cur));
  }

  const TIP_W = 220;
  const tipLeft = tip ? Math.min(Math.max(tip.x - TIP_W / 2, 0), Math.max(tip.width - TIP_W, 0)) : 0;

  return (
    <section className={`pl-group pl-group--${id}`} aria-labelledby={`pl-group-${id}`} ref={box}>
      <h2 className="pl-group__title" id={`pl-group-${id}`}>
        {t(`landing.${id}.title`)}
      </h2>
      <ul className="pl-chips">
        {tiles.map(({ key, Icon }, i) => {
          const descId = `pl-${id}-${key}-desc`;
          return (
            <li key={key} style={{ '--i': i + (id === 'brand' ? 4 : 0) }}>
              <button
                type="button"
                className={`pl-chip${tip?.key === key ? ' is-open' : ''}`}
                data-tile={key}
                aria-describedby={descId}
                onMouseEnter={(e) => show(key, e.currentTarget)}
                onMouseLeave={() => hide(key)}
                onFocus={(e) => show(key, e.currentTarget)}
                onBlur={() => hide(key)}
                onClick={(e) => (tip?.key === key ? hide(key) : show(key, e.currentTarget))}
              >
                <span className="pl-chip__icon" aria-hidden="true">
                  <Icon />
                </span>
                <span className="pl-chip__title">{t(`landing.${id}.${key}.title`)}</span>
                <span className="pl-sr pl-chip__desc" id={descId}>
                  {t(`landing.${id}.${key}.body`)}
                </span>
              </button>
            </li>
          );
        })}
      </ul>
      {tip && (
        <div className="pl-tip" role="presentation" aria-hidden="true" style={{ left: tipLeft, top: tip.top, width: TIP_W }}>
          {t(`landing.${id}.${tip.key}.body`)}
        </div>
      )}
    </section>
  );
}

/// What the visitor gets (blue): the chips that float over the webshop
/// window's edge — they are about that product page. What the brand gets
/// (violet): under the sign-in card. A phone lists both under the window.
export function BenefitGroup({ id }) {
  return <TileGroup id={id} tiles={id === 'brand' ? BRAND_TILES : VISITOR_TILES} />;
}
